import React, { useState, useEffect, useCallback, useMemo } from "react";
import { KalendusWrapper } from "../components/KalendusWrapper";
import { CalendarSidebar } from "../components/CalendarSidebar";
import { EventModal } from "../components/EventModal";
import { CommandPalette } from "../components/CommandPalette";
import { useEventStore } from "../stores/eventStore";
import { useCalendarStore } from "../stores/calendarStore";
import { useGoogleSyncStore } from "../stores/googleSyncStore";
import { useServerSync } from "../hooks/useServerSync";
import { eventToEntry, getDateRangeForView, type CalendarDate } from "../lib/dateUtils";
import type { ApiCalendarEntry, ApiDate } from "../lib/types";

export function CalendarPage() {
  const events = useEventStore((s) => s.events);
  const fetchRange = useEventStore((s) => s.fetchRange);
  const calendars = useCalendarStore((s) => s.calendars);
  const visibility = useCalendarStore((s) => s.visibility);
  const fetchCalendars = useCalendarStore((s) => s.fetch);
  const fetchGoogleStatus = useGoogleSyncStore((s) => s.fetchStatus);

  const [currentDate, setCurrentDate] = useState<CalendarDate>(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
  });
  const [currentView, setCurrentView] = useState("month");
  const [modalState, setModalState] = useState<{
    open: boolean;
    mode: "view" | "create" | "edit";
    event?: ApiCalendarEntry | null;
    defaultDate?: ApiDate;
  }>({ open: false, mode: "create" });

  const visibleIds = useMemo(
    () => calendars.filter((c) => visibility[c.id] !== false).map((c) => c.id),
    [calendars, visibility],
  );

  useServerSync(visibleIds);

  useEffect(() => {
    fetchCalendars();
    fetchGoogleStatus();
  }, [fetchCalendars, fetchGoogleStatus]);

  // Refresh sync status periodically so the in-app error indicator
  // reflects results from the background scheduler within ~a minute.
  useEffect(() => {
    const id = setInterval(() => {
      fetchGoogleStatus();
    }, 60_000);
    const onFocus = () => fetchGoogleStatus();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [fetchGoogleStatus]);

  useEffect(() => {
    if (visibleIds.length === 0) return;
    const range = getDateRangeForView(currentDate, currentView);
    fetchRange(visibleIds, range.start, range.end);
  }, [currentDate.year, currentDate.month, currentView, visibleIds.join(","), fetchRange]);

  const entries = useMemo(() => {
    const visSet = new Set(visibleIds);
    return events.filter((e) => visSet.has(e.calendarId)).map(eventToEntry);
  }, [events, visibleIds]);

  const eventLookup = useMemo(() => {
    const map = new Map<string, ApiCalendarEntry>();
    for (const event of events) {
      const key = `${event.heading}-${event.date.start.year}-${event.date.start.month}-${event.date.start.day}-${event.time?.start.hour ?? "all"}-${event.time?.start.minute ?? "day"}`;
      map.set(key, event);
    }
    return map;
  }, [events]);

  const handleActiveDateChange = useCallback((date: CalendarDate, view: string) => {
    setCurrentDate(date);
    setCurrentView(view);
  }, []);

  const handleEventClick = useCallback(
    (detail: {
      heading: string;
      content: string;
      date?: CalendarDate;
      time?: { hour: number; minute: number };
    }) => {
      if (detail.date) {
        const key = `${detail.heading}-${detail.date.year}-${detail.date.month}-${detail.date.day}-${detail.time?.hour ?? "all"}-${detail.time?.minute ?? "day"}`;
        const event = eventLookup.get(key);
        if (event) {
          setModalState({ open: true, mode: "view", event });
          return;
        }
      }
      const event = events.find((e) => e.heading === detail.heading);
      if (event) {
        setModalState({ open: true, mode: "view", event });
      }
    },
    [events, eventLookup],
  );

  const openNewEvent = useCallback(() => {
    setModalState({
      open: true,
      mode: "create",
      defaultDate: currentDate,
    });
  }, [currentDate]);

  const closeModal = useCallback(() => {
    setModalState({ open: false, mode: "create" });
  }, []);

  const shiftMonth = useCallback((delta: number) => {
    setCurrentDate((prev) => {
      const next = new Date(prev.year, prev.month - 1 + delta, 1);
      return { year: next.getFullYear(), month: next.getMonth() + 1, day: 1 };
    });
  }, []);

  // Listen for "go to today" from command palette
  useEffect(() => {
    const handler = () => {
      const now = new Date();
      setCurrentDate({ year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() });
    };
    window.addEventListener("calendar:today", handler);
    return () => window.removeEventListener("calendar:today", handler);
  }, []);

  // Listen for "navigate to a specific date" from search results
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { year: number; month: number; day: number }
        | undefined;
      if (detail && typeof detail.year === "number") {
        setCurrentDate({ year: detail.year, month: detail.month, day: detail.day });
      }
    };
    window.addEventListener("calendar:navigate-to", handler);
    return () => window.removeEventListener("calendar:navigate-to", handler);
  }, []);

  return (
    <div className="planner-shell">
      <aside className="planner-rail" aria-label="Calendar controls">
        <CalendarSidebar
          activeDate={currentDate}
          onSelectDate={setCurrentDate}
          onNavigateMonth={shiftMonth}
        />
      </aside>

      <main className="planner-main">
        <div className="calendar-clip">
          <KalendusWrapper
            entries={entries}
            activeDate={currentDate}
            onActiveDateChange={handleActiveDateChange}
            onEventClick={handleEventClick}
          />
        </div>
        <button
          className="fab"
          onClick={openNewEvent}
          aria-label="New event"
        >
          +
        </button>
      </main>

      {modalState.open && (
        <EventModal
          event={modalState.event}
          defaultDate={modalState.defaultDate}
          mode={modalState.mode}
          onClose={closeModal}
        />
      )}

      <CommandPalette onNewEvent={openNewEvent} />
    </div>
  );
}
